import { useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { download } from "@/admin/hr/common";
import { Loading, Table, Title, fmtDT, todayTR, useData } from "@/ops/ui";
import { BACKEND } from "@/lib/api";

const ST_CLS = { on_time: "bg-emerald-50 text-emerald-700", late: "bg-orange-50 text-orange-700", missing: "bg-red-50 text-red-700", pending: "bg-zinc-100 text-zinc-500" };
const LOC = { ok: "Yarıçap içinde", outside: "Yarıçap dışında", no_location: "Konum alınamadı", no_reference: "Referans konum yok" };
const SUM = [["on_time", "Zamanında"], ["late", "Gecikmeli"], ["missing", "Eksik"], ["pending", "Bekleniyor"]];

const badge = (r) => (r.status_label === "Gecikiyor" ? ST_CLS.late : ST_CLS[r.status]);
const locText = (s) => `${LOC[s.location_status] || ""}${s.distance_m != null ? ` (${Math.round(s.distance_m)} m)` : ""}`;

function SummaryCards({ summary }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {SUM.map(([k, l]) => <div key={k} className={`rounded-lg p-3 ${ST_CLS[k]}`} data-testid={`report-sum-${k}`}><p className="text-2xl font-extrabold">{summary[k]}</p><p className="text-sm">{l}</p></div>)}
    </div>
  );
}

function PhotoLink({ scan }) {
  return <a className="ml-1 text-blue-700 underline" target="_blank" rel="noreferrer" href={`${BACKEND}/api/ops/admin/scans/${scan.id}/photo`}>Fotoğraf</a>;
}

function RoundRow({ r, people, photos }) {
  const s = r.scan;
  return (
    <tr>
      <td className="px-3 py-2 font-semibold">{r.planned}</td><td className="px-3 py-2">{r.patrol}</td><td className="px-3 py-2">{r.checkpoint}</td>
      <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge(r)}`}>{r.status_label}</span></td>
      <td className="whitespace-nowrap px-3 py-2">{s ? fmtDT(s.at) : "-"}</td>
      {people && <td className="px-3 py-2">{s?.employee_name || "-"}</td>}
      <td className="px-3 py-2 text-xs">{s ? locText(s) : "-"}</td>
      <td className="px-3 py-2 text-xs text-orange-700">{(s?.flags || []).join(", ")}{photos && s?.has_photo && <PhotoLink scan={s} />}</td>
    </tr>
  );
}

function FlaggedTable({ rows, people }) {
  if (!rows.length) return null;
  return (
    <div className="mt-6"><h2 className="mb-2 font-bold">Sıra dışı / uyarılı okutmalar ({rows.length})</h2>
      <Table head={["Zaman", "Nokta", ...(people ? ["Personel"] : []), "Uyarılar"]} testid="flagged-table">
        {rows.map((s) => <tr key={s.id}><td className="whitespace-nowrap px-3 py-2">{fmtDT(s.at)}</td><td className="px-3 py-2">{s.checkpoint}</td>{people && <td className="px-3 py-2">{s.employee_name}</td>}<td className="px-3 py-2 text-xs text-orange-700">{s.flags.join(", ")}</td></tr>)}
      </Table>
    </div>
  );
}

export function ReportView({ rep, people = true, photos = false }) {
  if (!rep) return <Loading />;
  return (
    <div>
      <SummaryCards summary={rep.summary} />
      <Table head={["Plan", "Güzergâh", "Nokta", "Durum", "Okutma", ...(people ? ["Personel"] : []), "Konum", "Uyarı"]} testid="patrol-report-table">
        {rep.rounds.map((r) => <RoundRow key={`${r.patrol}-${r.checkpoint}-${r.planned}`} r={r} people={people} photos={photos} />)}
        {!rep.rounds.length && <tr><td colSpan={8} className="p-6 text-center text-zinc-500">Bu gün için planlanmış kontrol yok.</td></tr>}
      </Table>
      <FlaggedTable rows={rep.flagged} people={people} />
    </div>
  );
}

export default function PatrolReport() {
  const [projects] = useData("/ops/admin/projects");
  const [pid, setPid] = useState("");
  const [day, setDay] = useState(todayTR());
  const [rep] = useData(pid ? "/ops/admin/reports/patrol" : null, { project_id: pid, day });
  return (
    <div>
      <Title title="Devriye Raporu" desc="Planlanan ve gerçekleşen kontroller; geciken, eksik ve uyarılı okutmalar.">
        {pid && <button onClick={() => download("/ops/admin/reports/patrol.xlsx", `devriye-${day}.xlsx`, { params: { project_id: pid, day } })} className="btn-outline !py-2" data-testid="patrol-report-xlsx"><FileSpreadsheet className="h-4 w-4" /> Excel</button>}
      </Title>
      <div className="mb-4 flex flex-wrap gap-3">
        <select value={pid} onChange={(e) => setPid(e.target.value)} className="field-input max-w-sm" data-testid="patrol-report-project"><option value="">Proje seçin...</option>{(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="field-input max-w-[200px]" data-testid="patrol-report-date" />
      </div>
      {pid ? <ReportView rep={rep} photos /> : <p className="text-sm text-zinc-500">Rapor için proje seçin.</p>}
    </div>
  );
}
